import { Injectable, Injector, NgZone } from '@angular/core';
import {
    Observable,
    BehaviorSubject,
    from,
    of
} from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession, signOut, signInWithRedirect } from 'aws-amplify/auth';
import { PublicConfigStore } from '../public-config.store';
import { AccountService, IAccountUser } from './account.service';

@Injectable({
   providedIn: 'root'
})
export class AuthService {
    public loggedUser: BehaviorSubject<ICurrentUser>;

    constructor(
        private configStore: PublicConfigStore,
        private injector: Injector,
        private zone: NgZone) {
        this.loggedUser = new BehaviorSubject<ICurrentUser>(this.emptyUser());
    }

    public async init(): Promise<any> {
        console.log('Auth Service init');
        return getCurrentUser()
            .then(async () => {
                console.log('Auth Service init callback ');
                const userAttributes = await fetchUserAttributes();
                this.loggedUser.next({
                    ...this.emptyUser(),
                    email: userAttributes.email || null,
                    authenticated: true,
                });
                console.log('Auth Service init callback complete');
            })
            .catch(err => {
                console.log('Auth init error', err);
            })
            .finally(() => {
                console.log('Auth Service init callback finally');
                Hub.listen('auth', data => {
                    this.onAuthEvent(data.payload);
                });
            });
    }

    public async refreshTokens(): Promise<any> {
        console.log('Auth Service refresh');
        await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        await fetchAuthSession({ forceRefresh: true });
        this.loggedUser.next({
            ...this.emptyUser(),
            email: userAttributes.email || null,
            authenticated: true,
        });
        const user = await this.injector.get(AccountService).loadCurrentUser();
        this.applyAccount(user);
        console.log('Auth Service refresh callback complete');
    }

    public applyAccount(user: IAccountUser | null): void {
        const current = this.loggedUser.value;
        if (!user) {
            this.loggedUser.next({
                ...this.emptyUser(),
                email: current.email,
                authenticated: current.authenticated,
            });
            return;
        }

        this.loggedUser.next({
            id: user.id,
            email: user.email,
            displayName: user.displayName,
            authenticated: true,
            inGroup: user.inGroup,
            isAdmin: user.isAdmin,
        });
    }

    public login() {
        console.log('LOGIN');
        setTimeout(() => {
            this.configStore.getData().subscribe(() => {
                signInWithRedirect()
            });
        },
        2000);
    }

    public isAuthenticated(): Observable<boolean> {
        return from(getCurrentUser()).pipe(
            map(() => {
                return true;
            }),
            catchError(() => {
                return of(false);
            })
        );
    }

    public getToken(): Observable<string> {
        if (!this.loggedUser.value?.authenticated) {
            return of('');
        }
        const result = new Observable<string>(observer => {
            fetchAuthSession().then(
                data => {
                    const jwt = data.tokens?.idToken?.toString() || '';
                    observer.next(jwt);
                    observer.complete();
                },
                () => {
                    observer.next('');
                    observer.complete();
                }
            );
        });
        return result;
    }

    public signOut() {
        from(signOut()).subscribe({
            next: () => {},
            error: error => console.log(error)
        });
    }

    public onAuthEvent(payload: any) {
        if (payload.event === 'signIn' || payload.event === 'signInWithRedirect') {
            setTimeout(() => {
                this.zone.run(() => {
                    void this.loadAccountAfterSignIn();
                });
            }, 1);
        }
        if (payload.event === 'oAuthSignOut' || payload.event === 'signedOut') {
            this.zone.run(() => {
                this.loggedUser.next(this.emptyUser());
            });
        }
    }

    private async loadAccountAfterSignIn(): Promise<void> {
        try {
            const userAttributes = await fetchUserAttributes();
            this.loggedUser.next({
                ...this.emptyUser(),
                email: userAttributes.email || null,
                authenticated: true,
            });
            const user = await this.injector.get(AccountService).loadCurrentUser();
            this.applyAccount(user);
        } catch (error) {
            console.log('Auth sign-in account load error', error);
            this.loggedUser.next(this.emptyUser());
        }
    }

    private emptyUser(): ICurrentUser {
        return {
            id: null,
            email: null,
            displayName: null,
            authenticated: false,
            inGroup: false,
            isAdmin: false,
        };
    }
}

export interface ICurrentUser {
  id: string | null;
  email: string | null;
  displayName: string | null;
  authenticated: boolean;
  inGroup: boolean;
  isAdmin: boolean;
}
